import React, {
  useRef,
  useEffect,
  useId,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import PropTypes from 'prop-types'
import { IconExpandMoreDefault } from '../Icons'
import { useAccordionContext } from './index'

export interface ItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  uuid: string | number
  label: React.ReactElement | string
  headingAriaLevel?: number
  headingClassName?: string
  buttonClassName?: string
  itemPanelClassName?: string
  iconClassName?: string
  expandButtonSize?: 'sm' | 'md' | 'lg' | 'scale-8' | 'scale-10' | number
  children: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const Item = ({
  uuid,
  className = '',
  label,
  children,
  headingAriaLevel = 3, // Level 3 is appropriate for interactive components like accordions that are usually nested within a page's content hierarchy
  headingClassName = '',
  buttonClassName = '',
  itemPanelClassName = '',
  iconClassName = '',
  expandButtonSize = 'md',
  onClick,
  ...props
}: ItemProps) => {
  const {
    expandedItems,
    toggleItem,
    registerItem,
    unregisterItem,
    focusItem,
    getItemIds,
    transition,
    iconTransition,
    sequentialAnimation,
    bodyAnimation,
    iconAnimation,
  } = useAccordionContext()

  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const headingId = useId()
  const isExpanded = expandedItems.has(uuid)

  // Track cumulative rotation for continuous rotation animation
  const [rotationDegrees, setRotationDegrees] = useState(0)
  const prevExpandedRef = useRef(isExpanded)

  // Calculate transition timing
  const iconTransitionDuration = iconTransition.duration || 0.2
  const iconTransitionEase = iconTransition.ease || 'easeOut'

  // Calculate body animation delay for sequential animation
  const bodyDelay = sequentialAnimation ? iconTransitionDuration : 0

  // Update rotation when expand state changes
  useEffect(() => {
    if (iconAnimation === 'rotate' && prevExpandedRef.current !== isExpanded) {
      setRotationDegrees((prev) => prev + 180)
      prevExpandedRef.current = isExpanded
    }
  }, [isExpanded, iconAnimation])

  // Calculate icon rotation for 'rotate' animation (continuous forward rotation)
  const iconRotation = useMemo(() => {
    if (iconAnimation === 'rotate') {
      return rotationDegrees
    }
    return 0
  }, [iconAnimation, rotationDegrees])

  // Calculate icon scale for 'flip' animation
  const iconScale = useMemo(() => {
    if (iconAnimation === 'flip') {
      return isExpanded ? -1 : 1
    }
    return 1
  }, [iconAnimation, isExpanded])

  // Generate animation variants based on bodyAnimation type
  const animationVariants = useMemo(() => {
    if (typeof bodyAnimation === 'object' && bodyAnimation !== null) {
      // Custom variants provided
      return bodyAnimation as Variants
    }

    // Predefined variants
    switch (bodyAnimation) {
      case 'heightFade':
        return {
          collapsed: {
            height: 0,
            opacity: 0,
          },
          expanded: {
            height: 'auto',
            opacity: 1,
          },
        }
      case 'heightSlide':
        return {
          collapsed: {
            height: 0,
            opacity: 0,
            y: -10,
          },
          expanded: {
            height: 'auto',
            opacity: 1,
            y: 0,
          },
        }
      case 'height':
      default:
        return {
          collapsed: {
            height: 0,
          },
          expanded: {
            height: 'auto',
          },
        }
    }
  }, [bodyAnimation])

  // Register/unregister this item with the accordion context
  useEffect(() => {
    registerItem(uuid, buttonRef)
    return () => unregisterItem(uuid)
  }, [uuid, registerItem, unregisterItem])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      toggleItem(uuid)
      onClick?.(event)
    },
    [toggleItem, uuid, onClick],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const itemIds = getItemIds()
      const currentIndex = itemIds.indexOf(uuid)

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          event.preventDefault()
          const nextIndex = (currentIndex + 1) % itemIds.length
          focusItem(itemIds[nextIndex])
          break
        }
        case 'ArrowUp':
        case 'ArrowLeft': {
          event.preventDefault()
          const prevIndex =
            currentIndex === 0 ? itemIds.length - 1 : currentIndex - 1
          focusItem(itemIds[prevIndex])
          break
        }
        case 'Home':
          event.preventDefault()
          focusItem(itemIds[0])
          break
        case 'End':
          event.preventDefault()
          focusItem(itemIds[itemIds.length - 1])
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          toggleItem(uuid)
          break
        default:
          // Do nothing for other keys
          break
      }
    },
    [getItemIds, uuid, focusItem, toggleItem],
  )

  return (
    <div className={`mc-accordion__item ${className}`} {...props}>
      <div
        role='heading'
        aria-level={headingAriaLevel}
        className={headingClassName}
      >
        <button
          ref={buttonRef}
          type='button'
          id={headingId}
          className={`mc-accordion__button mc-p-4 mc-mb-1 ${buttonClassName}`}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          style={{ letterSpacing: '0.32px' }}
        >
          {label}
          <motion.div
            animate={{
              rotate: iconRotation,
              scaleY: iconScale,
            }}
            transition={{
              duration: iconTransitionDuration,
              ease: iconTransitionEase,
            }}
            className='mc-ml-5 mc-accordion__icon'
          >
            <IconExpandMoreDefault
              size={expandButtonSize}
              className={iconClassName}
            />
          </motion.div>
        </button>
      </div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            id={panelId}
            role='region'
            aria-labelledby={headingId}
            data-accordion-component='AccordionItemPanel'
            style={{ overflow: 'hidden' }}
            initial='collapsed'
            animate='expanded'
            exit='collapsed'
            variants={animationVariants}
            transition={{
              duration: transition.duration || 0.2,
              ease: transition.ease || 'easeInOut',
              delay: bodyDelay,
            }}
          >
            <div className={`mc-p-4 mc-pt-0 ${itemPanelClassName}`}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

Item.propTypes = {
  uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  className: PropTypes.string,
  label: PropTypes.node.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  headingAriaLevel: PropTypes.number,
  headingClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  itemPanelClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  expandButtonSize: PropTypes.oneOfType([
    PropTypes.oneOf(['sm', 'md', 'lg', 'scale-8', 'scale-10']),
    PropTypes.number,
  ]),
  onClick: PropTypes.func,
}

export default Item
